import React from "react";

// Helper component for Contact Information
const ContactInfo = ({ teamMember }) => {
  if (!teamMember?.contact) return null;
  
  return (
    <div className="mb-4">
      <h5 className="mb-3" style={{ color: "#2d3748", fontWeight: "700", fontSize: "1.1rem" }}>
        Contact Information
      </h5>
      <ul className="list-unstyled" style={{ lineHeight: "2" }}>
        {teamMember.contact.email && (
          <li className="mb-2">
            <i className="fas fa-envelope mr-2" style={{ color: "#667eea", width: "20px" }}></i>
            <a href={`mailto:${teamMember.contact.email}`} style={{ color: "#4a5568" }}>
              {teamMember.contact.email}
            </a>
          </li>
        )}
        {teamMember.contact.phone && (
          <li className="mb-2">
            <i className="fas fa-phone mr-2" style={{ color: "#667eea", width: "20px" }}></i>
            <a href={`tel:${teamMember.contact.phone}`} style={{ color: "#4a5568" }}>
              {teamMember.contact.phone}
            </a>
          </li>
        )}
        {teamMember.contact.location && (
          <li className="mb-2">
            <i className="fas fa-map-marker-alt mr-2" style={{ color: "#667eea", width: "20px" }}></i>
            <span style={{ color: "#4a5568" }}>{teamMember.contact.location}</span>
          </li>
        )}
      </ul>
    </div>
  );
};

// Helper component for Social Media Links
const SocialLinks = ({ teamMember }) => {
  if (!teamMember?.social) return null;
  
  const socialPlatforms = [
    { key: 'linkedin', icon: 'fab fa-linkedin-in', label: 'LinkedIn' },
    { key: 'twitter', icon: 'fab fa-twitter', label: 'Twitter' },
    { key: 'facebook', icon: 'fab fa-facebook-f', label: 'Facebook' },
    { key: 'github', icon: 'fab fa-github', label: 'GitHub' },
    { key: 'instagram', icon: 'fab fa-instagram', label: 'Instagram' },
  ];
  
  const availableLinks = socialPlatforms.filter(platform => teamMember.social && teamMember.social[platform.key]);
  
  if (availableLinks.length === 0) return null;
  
  return (
    <div className="mb-4">
      <h5 className="mb-3" style={{ color: "#2d3748", fontWeight: "700", fontSize: "1.1rem" }}>
        Connect
      </h5>
      <ul className="list-inline mb-0">
        {availableLinks.map((platform) => (
          <li key={platform.key} className="list-inline-item mr-3">
            <a
              href={teamMember.social[platform.key]}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#667eea",
                fontSize: "1.5rem",
                transition: "all 0.3s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.color = "#764ba2";
                e.target.style.transform = "scale(1.2)";
              }}
              onMouseLeave={(e) => {
                e.target.style.color = "#667eea";
                e.target.style.transform = "scale(1)";
              }}
              title={platform.label}
            >
              <i className={platform.icon}></i>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Helper component for Activities & Skills
const ActivitiesSkills = ({ teamMember }) => {
  if (!teamMember?.activities_skills || teamMember.activities_skills.length === 0) return null;
  
  return (
    <div className="mb-4">
      <h5 className="mb-3" style={{ color: "#2d3748", fontWeight: "700", fontSize: "1.1rem" }}>
        Activities & Skills
      </h5>
      <ul className="list-unstyled">
        {teamMember.activities_skills.map((item, index) => (
          <li key={index} className="mb-3">
            <strong style={{ color: "#2d3748" }}>{item.heading}</strong>
            <p className="mb-0 mt-1" style={{ color: "#4a5568", lineHeight: "1.7" }}>
              {item.desc}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Helper component for Professional Skills
const ProfessionalSkills = ({ teamMember }) => {
  if (!teamMember?.professional_skills || teamMember.professional_skills.length === 0) return null;
  
  return (
    <div className="mb-4">
      <h5 className="mb-3" style={{ color: "#2d3748", fontWeight: "700", fontSize: "1.1rem" }}>
        Professional Skills
      </h5>
      {teamMember.professional_skills.map((skill, index) => (
        <div key={index} className="mb-3">
          <div className="d-flex justify-content-between mb-2">
            <span style={{ color: "#2d3748", fontWeight: "600" }}>{skill.heading}</span>
            <span style={{ color: "#667eea", fontWeight: "600" }}>{skill.percentage}</span>
          </div>
          <div className="progress" style={{ height: "8px", borderRadius: "10px", background: "#e2e8f0" }}>
            <div
              className="progress-bar"
              role="progressbar"
              style={{
                width: skill.percentage,
                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "10px"
              }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Helper component for Education
const Education = ({ teamMember }) => {
  if (!teamMember?.education || teamMember.education.length === 0) return null;
  
  return (
    <div className="mb-4">
      <h5 className="mb-3" style={{ color: "#2d3748", fontWeight: "700", fontSize: "1.1rem" }}>
        Education
      </h5>
      <ul className="list-unstyled">
        {teamMember.education.map((edu, index) => (
          <li key={index} className="mb-3 pb-3" style={{ borderBottom: index < teamMember.education.length - 1 ? "1px solid #e2e8f0" : "none" }}>
            <strong style={{ color: "#2d3748", display: "block" }}>{edu.degree || edu.institution}</strong>
            {edu.institution && edu.degree && (
              <span style={{ color: "#667eea", fontSize: "0.95rem" }}>{edu.institution}</span>
            )}
            {edu.year && (
              <span style={{ color: "#718096", fontSize: "0.9rem", display: "block", marginTop: "4px" }}>
                {edu.year}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Helper component for Experience
const Experience = ({ teamMember }) => {
  if (!teamMember?.experience || teamMember.experience.length === 0) return null;
  
  return (
    <div className="mb-4">
      <h5 className="mb-3" style={{ color: "#2d3748", fontWeight: "700", fontSize: "1.1rem" }}>
        Experience
      </h5>
      <ul className="list-unstyled">
        {teamMember.experience.map((exp, index) => (
          <li key={index} className="mb-4 pb-3" style={{ borderBottom: index < teamMember.experience.length - 1 ? "1px solid #e2e8f0" : "none" }}>
            <strong style={{ color: "#2d3748", display: "block" }}>{exp.position || exp.title}</strong>
            {exp.company && (
              <span style={{ color: "#667eea", fontSize: "0.95rem" }}>{exp.company}</span>
            )}
            {exp.period && (
              <span style={{ color: "#718096", fontSize: "0.9rem", display: "block", marginTop: "4px" }}>
                {exp.period}
              </span>
            )}
            {exp.description && (
              <p className="mt-2 mb-0" style={{ color: "#4a5568", fontSize: "0.95rem", lineHeight: "1.7" }}>
                {exp.description}
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Helper component for Certifications
const Certifications = ({ teamMember }) => {
  if (!teamMember?.certifications || teamMember.certifications.length === 0) return null;
  
  return (
    <div className="mb-4">
      <h5 className="mb-3" style={{ color: "#2d3748", fontWeight: "700", fontSize: "1.1rem" }}>
        Certifications
      </h5>
      <ul className="list-unstyled">
        {teamMember.certifications.map((cert, index) => (
          <li key={index} className="mb-2">
            <i className="fas fa-certificate mr-2" style={{ color: "#667eea" }}></i>
            <span style={{ color: "#4a5568" }}>{cert.name || cert}</span>
            {cert.issuer && (
              <span style={{ color: "#718096", fontSize: "0.9rem" }}> - {cert.issuer}</span>
            )}
            {cert.year && (
              <span style={{ color: "#718096", fontSize: "0.9rem" }}> ({cert.year})</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Layout 1: Card-Based Layout
export const TeamDetailCardLayout = ({ teamMember }) => {
  return (
    <section className="team-single-section ptb-100" style={{ background: "#f8f9fa" }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card shadow-lg border-0" style={{ borderRadius: "20px", overflow: "hidden" }}>
              <div className="row g-0">
                <div className="col-md-5" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
                  <div className="d-flex align-items-center justify-content-center h-100 p-4">
                    <div
                      className="team-detail-image"
                      style={{
                        width: "280px",
                        height: "280px",
                        borderRadius: "50%",
                        backgroundImage: `url(${teamMember?.image})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        border: "8px solid rgba(255,255,255,0.3)",
                        boxShadow: "0 10px 40px rgba(0,0,0,0.2)"
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-7">
                  <div className="card-body p-5">
                    <div className="mb-3">
                      <span className="badge" style={{ background: "#667eea", padding: "8px 16px", fontSize: "0.85rem" }}>
                        {teamMember?.role || "--"}
                      </span>
                    </div>
                    <h2 className="mb-3" style={{ color: "#2d3748", fontWeight: "800" }}>
                      {teamMember?.name}
                    </h2>
                    <p className="text-muted mb-4" style={{ lineHeight: "1.8", fontSize: "1.05rem" }}>
                      {teamMember?.about || teamMember?.description || "--"}
                    </p>
                    
                    <ContactInfo teamMember={teamMember} />
                    <SocialLinks teamMember={teamMember} />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Additional Sections */}
            <div className="row mt-4">
              <div className="col-md-6">
                <ActivitiesSkills teamMember={teamMember} />
                <ProfessionalSkills teamMember={teamMember} />
              </div>
              <div className="col-md-6">
                <Education teamMember={teamMember} />
                <Experience teamMember={teamMember} />
                <Certifications teamMember={teamMember} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Layout 2: Minimal Clean Layout
export const TeamDetailMinimalLayout = ({ teamMember }) => {
  return (
    <section className="team-single-section ptb-100" style={{ background: "#ffffff" }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8 text-center mb-5">
            <div
              className="mx-auto mb-4"
              style={{
                width: "200px",
                height: "200px",
                borderRadius: "50%",
                backgroundImage: `url(${teamMember?.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                border: "4px solid #e2e8f0"
              }}
            />
            <h1 className="mb-2" style={{ color: "#2d3748", fontWeight: "700", fontSize: "2.5rem" }}>
              {teamMember?.name}
            </h1>
            <p className="text-muted mb-4" style={{ fontSize: "1.2rem" }}>
              {teamMember?.role || "--"}
            </p>
            <div className="mx-auto mb-4" style={{ maxWidth: "600px" }}>
              <p style={{ color: "#4a5568", lineHeight: "1.9", fontSize: "1.1rem" }}>
                {teamMember?.about || teamMember?.description || "--"}
              </p>
            </div>
            <SocialLinks teamMember={teamMember} />
          </div>
        </div>
        
        {/* Additional Sections */}
        <div className="row justify-content-center mt-5">
          <div className="col-lg-10">
            <div className="row">
              <div className="col-md-6 mb-4">
                <ContactInfo teamMember={teamMember} />
                <ActivitiesSkills teamMember={teamMember} />
              </div>
              <div className="col-md-6 mb-4">
                <ProfessionalSkills teamMember={teamMember} />
                <Education teamMember={teamMember} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-4">
                <Experience teamMember={teamMember} />
              </div>
              <div className="col-md-6 mb-4">
                <Certifications teamMember={teamMember} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Layout 3: Split Screen Layout
export const TeamDetailSplitLayout = ({ teamMember }) => {
  return (
    <>
      <section className="team-single-section ptb-100" style={{ background: "#ffffff" }}>
        <div className="container-fluid px-0">
          <div className="row g-0">
            <div className="col-lg-6" style={{ minHeight: "600px" }}>
              <div
                className="h-100"
                style={{
                  backgroundImage: `url(${teamMember?.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  position: "relative"
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)"
                  }}
                />
              </div>
            </div>
            <div className="col-lg-6 d-flex align-items-center">
              <div className="p-5" style={{ maxWidth: "600px", margin: "0 auto" }}>
                <span className="text-uppercase" style={{ color: "#667eea", fontSize: "0.85rem", fontWeight: "700", letterSpacing: "2px" }}>
                  {teamMember?.role || "--"}
                </span>
                <h1 className="my-4" style={{ color: "#2d3748", fontWeight: "800", fontSize: "3rem" }}>
                  {teamMember?.name}
                </h1>
                <p style={{ color: "#4a5568", lineHeight: "2", fontSize: "1.15rem" }}>
                  {teamMember?.about || teamMember?.description || "--"}
                </p>
                <div className="mt-4">
                  <ContactInfo teamMember={teamMember} />
                  <SocialLinks teamMember={teamMember} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Additional Sections */}
      <section className="ptb-100" style={{ background: "#f8f9fa" }}>
        <div className="container">
          <div className="row">
            <div className="col-md-6 mb-4">
              <ActivitiesSkills teamMember={teamMember} />
              <ProfessionalSkills teamMember={teamMember} />
            </div>
            <div className="col-md-6 mb-4">
              <Education teamMember={teamMember} />
              <Experience teamMember={teamMember} />
              <Certifications teamMember={teamMember} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// Layout 4: Hero Centered Layout
export const TeamDetailHeroLayout = ({ teamMember }) => {
  return (
    <>
      <section className="team-single-section ptb-100" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <div
                className="mx-auto mb-4"
                style={{
                  width: "250px",
                  height: "250px",
                  borderRadius: "50%",
                  backgroundImage: `url(${teamMember?.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  border: "8px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 15px 50px rgba(0,0,0,0.3)"
                }}
              />
              <h1 className="mb-3" style={{ color: "white", fontWeight: "800", fontSize: "3.5rem" }}>
                {teamMember?.name}
              </h1>
              <p className="mb-4" style={{ fontSize: "1.3rem", opacity: "0.9" }}>
                {teamMember?.role || "--"}
              </p>
              <div className="mx-auto mb-4" style={{ maxWidth: "700px" }}>
                <p style={{ lineHeight: "2", fontSize: "1.15rem", opacity: "0.95" }}>
                  {teamMember?.about || teamMember?.description || "--"}
                </p>
              </div>
              <div style={{ color: "white" }}>
                <SocialLinks teamMember={teamMember} />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Additional Sections */}
      <section className="ptb-100" style={{ background: "#ffffff" }}>
        <div className="container">
          <div className="row">
            <div className="col-md-6 mb-4">
              <ContactInfo teamMember={teamMember} />
              <ActivitiesSkills teamMember={teamMember} />
              <ProfessionalSkills teamMember={teamMember} />
            </div>
            <div className="col-md-6 mb-4">
              <Education teamMember={teamMember} />
              <Experience teamMember={teamMember} />
              <Certifications teamMember={teamMember} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

// Layout 5: Grid Cards Layout
export const TeamDetailGridLayout = ({ teamMember }) => {
  return (
    <section className="team-single-section ptb-100" style={{ background: "#f8f9fa" }}>
      <div className="container">
        <div className="row justify-content-center mb-5">
          <div className="col-lg-6 text-center">
            <div
              className="mx-auto mb-4"
              style={{
                width: "220px",
                height: "220px",
                borderRadius: "20px",
                backgroundImage: `url(${teamMember?.image})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                boxShadow: "0 10px 40px rgba(0,0,0,0.15)"
              }}
            />
            <h2 className="mb-2" style={{ color: "#2d3748", fontWeight: "800" }}>
              {teamMember?.name}
            </h2>
            <p className="text-muted mb-4">{teamMember?.role || "--"}</p>
            <SocialLinks teamMember={teamMember} />
          </div>
        </div>
        <div className="row justify-content-center mb-5">
          <div className="col-lg-10">
            <div className="card border-0 shadow-sm" style={{ borderRadius: "15px" }}>
              <div className="card-body p-5">
                <p style={{ color: "#4a5568", lineHeight: "1.9", fontSize: "1.1rem", margin: 0 }}>
                  {teamMember?.about || teamMember?.description || "--"}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Additional Sections */}
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="row">
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <div className="card-body p-4">
                    <ContactInfo teamMember={teamMember} />
                    <ActivitiesSkills teamMember={teamMember} />
                    <ProfessionalSkills teamMember={teamMember} />
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <div className="card-body p-4">
                    <Education teamMember={teamMember} />
                    <Experience teamMember={teamMember} />
                    <Certifications teamMember={teamMember} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Layout 6: Sidebar Layout
export const TeamDetailSidebarLayout = ({ teamMember }) => {
  return (
    <section className="team-single-section ptb-100" style={{ background: "#ffffff" }}>
      <div className="container">
        <div className="row">
          <div className="col-lg-4 mb-4 mb-lg-0">
            <div className="card border-0 shadow-sm sticky-top" style={{ top: "100px", borderRadius: "15px", overflow: "hidden" }}>
              <div
                className="card-img-top"
                style={{
                  height: "400px",
                  backgroundImage: `url(${teamMember?.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center"
                }}
              />
              <div className="card-body p-4 text-center">
                <h4 className="mb-2" style={{ color: "#2d3748", fontWeight: "700" }}>
                  {teamMember?.name}
                </h4>
                <p className="text-muted mb-3">{teamMember?.role || "--"}</p>
                <ContactInfo teamMember={teamMember} />
                <SocialLinks teamMember={teamMember} />
              </div>
            </div>
          </div>
          <div className="col-lg-8">
            <div className="ps-lg-4">
              <h1 className="mb-4" style={{ color: "#2d3748", fontWeight: "800", fontSize: "2.5rem" }}>
                {teamMember?.name}
              </h1>
              <p style={{ color: "#4a5568", lineHeight: "2", fontSize: "1.15rem", marginBottom: "2rem" }}>
                {teamMember?.about || teamMember?.description || "--"}
              </p>
              
              <ActivitiesSkills teamMember={teamMember} />
              <ProfessionalSkills teamMember={teamMember} />
              <Education teamMember={teamMember} />
              <Experience teamMember={teamMember} />
              <Certifications teamMember={teamMember} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Layout 7: Timeline Style Layout
export const TeamDetailTimelineLayout = ({ teamMember }) => {
  return (
    <section className="team-single-section ptb-100" style={{ background: "#f8f9fa" }}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="row align-items-center mb-5">
              <div className="col-md-4 text-center mb-4 mb-md-0">
                <div
                  className="mx-auto"
                  style={{
                    width: "200px",
                    height: "200px",
                    borderRadius: "50%",
                    backgroundImage: `url(${teamMember?.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    border: "5px solid #667eea",
                    boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)"
                  }}
                />
              </div>
              <div className="col-md-8">
                <h1 className="mb-3" style={{ color: "#2d3748", fontWeight: "800" }}>
                  {teamMember?.name}
                </h1>
                <p className="text-muted mb-3" style={{ fontSize: "1.2rem" }}>
                  {teamMember?.role || "--"}
                </p>
                <div style={{ width: "80px", height: "4px", background: "#667eea", borderRadius: "2px", marginBottom: "1rem" }}></div>
                <SocialLinks teamMember={teamMember} />
              </div>
            </div>
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
              <div className="card-body p-5">
                <p style={{ color: "#4a5568", lineHeight: "2", fontSize: "1.1rem", margin: 0 }}>
                  {teamMember?.about || teamMember?.description || "--"}
                </p>
              </div>
            </div>
            
            {/* Additional Sections */}
            <div className="row">
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <div className="card-body p-4">
                    <ContactInfo teamMember={teamMember} />
                    <ActivitiesSkills teamMember={teamMember} />
                    <ProfessionalSkills teamMember={teamMember} />
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <div className="card-body p-4">
                    <Education teamMember={teamMember} />
                    <Experience teamMember={teamMember} />
                    <Certifications teamMember={teamMember} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Layout 8: Modern Glassmorphism Layout
export const TeamDetailGlassLayout = ({ teamMember }) => {
  return (
    <>
      <section className="team-single-section ptb-100" style={{ 
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden"
      }}>
        <div className="container position-relative" style={{ zIndex: 2 }}>
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div 
                className="card border-0 p-5"
                style={{
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "25px",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
                }}
              >
                <div className="text-center mb-4">
                  <div
                    className="mx-auto"
                    style={{
                      width: "180px",
                      height: "180px",
                      borderRadius: "50%",
                      backgroundImage: `url(${teamMember?.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      border: "6px solid #667eea",
                      boxShadow: "0 10px 40px rgba(102, 126, 234, 0.4)"
                    }}
                  />
                </div>
                <div className="text-center">
                  <h2 className="mb-2" style={{ color: "#2d3748", fontWeight: "800" }}>
                    {teamMember?.name}
                  </h2>
                  <p className="text-muted mb-4" style={{ fontSize: "1.1rem" }}>
                    {teamMember?.role || "--"}
                  </p>
                  <p style={{ color: "#4a5568", lineHeight: "1.9", fontSize: "1.05rem", marginBottom: "1.5rem" }}>
                    {teamMember?.about || teamMember?.description || "--"}
                  </p>
                  <SocialLinks teamMember={teamMember} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Additional Sections */}
      <section className="ptb-100" style={{ background: "#f8f9fa" }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="row">
                <div className="col-md-6 mb-4">
                  <div 
                    className="card border-0 p-4"
                    style={{
                      background: "rgba(255, 255, 255, 0.9)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "20px",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.1)"
                    }}
                  >
                    <ContactInfo teamMember={teamMember} />
                    <ActivitiesSkills teamMember={teamMember} />
                    <ProfessionalSkills teamMember={teamMember} />
                  </div>
                </div>
                <div className="col-md-6 mb-4">
                  <div 
                    className="card border-0 p-4"
                    style={{
                      background: "rgba(255, 255, 255, 0.9)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "20px",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.1)"
                    }}
                  >
                    <Education teamMember={teamMember} />
                    <Experience teamMember={teamMember} />
                    <Certifications teamMember={teamMember} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
